import React, { useState, useEffect } from 'react';
import {
  History,
  Search,
  Shield,
  User,
  Clock,
  CheckCircle2,
  FileText,
  Filter,
} from 'lucide-react';
import { AuditLogItem } from '../types/index.ts';

export const AuditLogPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/audit-logs');
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) setLogs(data);
      }
    } catch (err) {
      console.warn('Audit logs fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filtered = (Array.isArray(logs) ? logs : []).filter((l) => {
    return (
      l.action.toLowerCase().includes(search.toLowerCase()) ||
      l.userName.toLowerCase().includes(search.toLowerCase()) ||
      l.module.toLowerCase().includes(search.toLowerCase()) ||
      (l.details && l.details.toLowerCase().includes(search.toLowerCase()))
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Audit Trail &amp; Governance
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-slate-100 text-slate-800">
              Immutable PostgreSQL Log
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Every critical action, status progression, payment entry, and approval is recorded with user identity and timestamp.
          </p>
        </div>

        <div className="text-xs text-slate-500 bg-white px-3 py-1.5 rounded-lg border border-slate-200">
          Showing <strong>{filtered.length}</strong> system event records
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-2 bg-white p-3 rounded-xl border border-slate-200">
        <Search className="w-4 h-4 text-slate-400 ml-1" />
        <input
          type="text"
          className="w-full text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden"
          placeholder="Filter audit events by user, action, module, or status change..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Audit Log Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Timestamp</th>
                <th className="py-3 px-4">User</th>
                <th className="py-3 px-4">Action</th>
                <th className="py-3 px-4">Module</th>
                <th className="py-3 px-4">Status Transition</th>
                <th className="py-3 px-4">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-sans">
              {filtered.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-3 px-4 text-slate-500 font-mono text-[11px] whitespace-nowrap">
                    {new Date(log.createdAt).toLocaleString('en-PK')}
                  </td>
                  <td className="py-3 px-4 font-semibold text-slate-900 whitespace-nowrap">
                    {log.userName}
                  </td>
                  <td className="py-3 px-4 font-medium text-blue-900">{log.action}</td>
                  <td className="py-3 px-4">
                    <span className="inline-block px-2 py-0.5 rounded bg-slate-100 font-mono text-[10px] text-slate-700 font-bold">
                      {log.module}
                    </span>
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    {log.oldStatus || log.newStatus ? (
                      <span className="text-xs">
                        <span className="text-slate-400">{log.oldStatus || 'Initial'}</span>
                        <span className="mx-1 text-slate-400">→</span>
                        <span className="font-bold text-slate-800">{log.newStatus}</span>
                      </span>
                    ) : (
                      <span className="text-slate-400 text-[11px]">Logged</span>
                    )}
                  </td>
                  <td className="py-3 px-4 max-w-sm truncate text-slate-600">{log.details || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
